import { PayableRow } from '@bariendo/lib/payables';
import dayjs from 'dayjs';
import { rowsToCsv } from '@/services/csv';
import { costProcedureLabel } from './costSchedule';

/** Payables (BAR-3046): what we owe, filtered and summed in the browser, as the prototype does. */

export const FILTER_ALL = 'all';

export type PayableTypeFilter = 'all' | 'facility' | 'anesthesia' | 'doctor' | 'pn';
export type PayableStatusChoice = 'open' | 'paid' | 'all';

export interface PayableFiltersState {
  type: PayableTypeFilter;
  /** A payee reference, or `all`. */
  payee: string;
  status: PayableStatusChoice;
}

export const DEFAULT_PAYABLE_FILTERS: PayableFiltersState = { type: 'all', payee: FILTER_ALL, status: 'open' };

const TYPE_OF: Record<PayableRow['feeType'], Exclude<PayableTypeFilter, 'all'>> = {
  FACILITY: 'facility',
  DAILY_MINIMUM_TOPUP: 'facility',
  ANESTHESIA: 'anesthesia',
  DOCTOR: 'doctor',
  PN_COMMISSION: 'pn',
  PN_CLAWBACK: 'pn',
};

const FEE_TYPE_LABEL: Record<PayableRow['feeType'], string> = {
  FACILITY: 'Facility',
  DAILY_MINIMUM_TOPUP: 'Daily minimum top-up',
  ANESTHESIA: 'Anesthesia',
  DOCTOR: 'Doctor',
  PN_COMMISSION: 'PN commission',
  PN_CLAWBACK: 'PN clawback',
};

const TYPE_ORDER: Record<Exclude<PayableTypeFilter, 'all'>, number> = { facility: 0, anesthesia: 1, doctor: 2, pn: 3 };

export function feeTypeLabel(row: Pick<PayableRow, 'feeType'>): string {
  return FEE_TYPE_LABEL[row.feeType];
}

export function isClawback(row: Pick<PayableRow, 'feeType'>): boolean {
  return row.feeType === 'PN_CLAWBACK';
}

/** What the line will pay: Finance's Actual, else the schedule; null when neither exists. */
export function actualAmount(row: Pick<PayableRow, 'actualAmount' | 'scheduledAmount'>): number | null {
  return row.actualAmount ?? row.scheduledAmount;
}

/** Amber with the schedule on hover when the Actual departs from it. */
export function actualDiffers(row: Pick<PayableRow, 'actualAmount' | 'scheduledAmount'>): boolean {
  return (
    row.actualAmount !== null &&
    row.scheduledAmount !== null &&
    Math.round(row.actualAmount * 100) !== Math.round(row.scheduledAmount * 100)
  );
}

/** A line Mark paid can take: unpaid, with an amount. */
export function isPayable(row: PayableRow): boolean {
  return row.status === 'UNPAID' && actualAmount(row) !== null;
}

/** The Payee filter's options for the Fee type chosen, by name. */
export function payeeOptions(rows: PayableRow[], type: PayableTypeFilter): { value: string; label: string }[] {
  const names = new Map<string, string>();
  for (const row of rows) {
    if (type === FILTER_ALL || TYPE_OF[row.feeType] === type) names.set(row.payeeRef, row.payeeName);
  }
  return [...names.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

function matchesTypeAndPayee(row: PayableRow, filters: Pick<PayableFiltersState, 'type' | 'payee'>): boolean {
  return (
    (filters.type === FILTER_ALL || TYPE_OF[row.feeType] === filters.type) &&
    (filters.payee === FILTER_ALL || row.payeeRef === filters.payee)
  );
}

/** Lines, newest date first, then by fee type, as the prototype sorts them. */
export function filterPayables(rows: PayableRow[], filters: PayableFiltersState): PayableRow[] {
  return rows
    .filter((row) => matchesTypeAndPayee(row, filters))
    .filter(
      (row) => filters.status === 'all' || (filters.status === 'open' ? row.status === 'UNPAID' : row.status === 'PAID')
    )
    .sort(
      (a, b) =>
        b.serviceDate.localeCompare(a.serviceDate) || TYPE_ORDER[TYPE_OF[a.feeType]] - TYPE_ORDER[TYPE_OF[b.feeType]]
    );
}

export function isFiltered(filters: PayableFiltersState): boolean {
  return (
    filters.type !== DEFAULT_PAYABLE_FILTERS.type ||
    filters.payee !== DEFAULT_PAYABLE_FILTERS.payee ||
    filters.status !== DEFAULT_PAYABLE_FILTERS.status
  );
}

export function sumActual(rows: PayableRow[]): number {
  return rows.reduce((sum, row) => sum + (actualAmount(row) ?? 0), 0);
}

export function formatPayableDate(date: string | null): string {
  return date ? dayjs(date).format('MMM D, YYYY') : '';
}

/** The current quarter's three months, for By month. */
export function quarterMonths(now: Date): string[] {
  const start = dayjs(now)
    .startOf('month')
    .subtract(dayjs(now).month() % 3, 'month');
  return [0, 1, 2].map((offset) => start.add(offset, 'month').format('YYYY-MM'));
}

export function quarterLabel(now: Date): string {
  return `Q${Math.floor(dayjs(now).month() / 3) + 1}`;
}

export interface ByMonthRow {
  name: string;
  total?: boolean;
  months: number[];
  quarter: number;
  open: number;
}

export interface ByMonthGroup {
  label: string;
  rows: ByMonthRow[];
}

const BY_MONTH_GROUPS: { label: string; matches: (row: PayableRow) => boolean }[] = [
  { label: 'Facility', matches: (row) => TYPE_OF[row.feeType] === 'facility' },
  { label: 'Anesthesia', matches: (row) => row.feeType === 'ANESTHESIA' },
  { label: 'Doctor', matches: (row) => row.feeType === 'DOCTOR' },
  { label: 'PN commission', matches: (row) => row.feeType === 'PN_COMMISSION' },
  { label: 'PN clawback', matches: (row) => row.feeType === 'PN_CLAWBACK' },
];

/** By month: payee by month for the Fee type and Payee filters, grouped by fee type, with totals and Open. */
export function buildByMonth(
  rows: PayableRow[],
  filters: Pick<PayableFiltersState, 'type' | 'payee'>,
  months: string[]
): ByMonthGroup[] {
  const lines = rows.filter((row) => matchesTypeAndPayee(row, filters));
  const row = (mine: PayableRow[], name: string, total?: boolean): ByMonthRow => ({
    name,
    total,
    months: months.map((month) => sumActual(mine.filter((line) => line.serviceDate.startsWith(month)))),
    quarter: sumActual(mine),
    open: sumActual(mine.filter((line) => line.status === 'UNPAID')),
  });
  return BY_MONTH_GROUPS.flatMap(({ label, matches }) => {
    const mine = lines.filter(matches);
    if (!mine.length) return [];
    const payees = [...new Map(mine.map((line) => [line.payeeRef, line.payeeName])).entries()];
    return [
      {
        label,
        rows: [
          ...payees.map(([ref, name]) =>
            row(
              mine.filter((line) => line.payeeRef === ref),
              name
            )
          ),
          row(mine, `${label} total`, true),
        ],
      },
    ];
  });
}

export const PAYABLE_CSV_HEADERS = [
  'Patient',
  'Procedure',
  'Payee',
  'Fee type',
  'Date',
  'Scheduled',
  'Actual',
  'Paid on',
  'Reference',
  'Note',
];

const csvMoney = (value: number | null) => (value === null ? '' : value.toFixed(2));

export function payablesCsv(rows: PayableRow[]): string {
  return rowsToCsv(
    PAYABLE_CSV_HEADERS,
    rows.map((row) => [
      row.patientName ?? '',
      row.procedure ? costProcedureLabel(row.procedure) : '',
      row.payeeName,
      feeTypeLabel(row),
      row.serviceDate,
      csvMoney(row.scheduledAmount),
      csvMoney(actualAmount(row)),
      row.paidOn ?? '',
      row.reference ?? '',
      row.note ?? '',
    ])
  );
}

export function payablesCsvFilename(now: Date): string {
  return `payables-${dayjs(now).format('YYYY-MM-DD')}.csv`;
}
